// Mirror of CommonPdfBusinessSkillsSection.jsx: per category, a 25% / 75% row;
// the right column holds competences two-per-line, each with a 5-dash rating
// (active dashes in the accent color, inactive in grey). A rule closes each
// category.

import { TableRow, VerticalAlign } from "docx";
import { INACTIVE_DASH } from "../../shared/wordTheme";
import { FONT_ARIAL } from "../../shared/wordTheme";
import { cell, dividerRule, frac, layoutTable, para, run, tableGap } from "../../shared/wordHelpers";

function dashRuns( rating, accent ) {
    return [ 1, 2, 3, 4, 5 ].map( value =>
        run( "-", { pt: 12, color: value <= ( rating ?? 0 ) ? accent : INACTIVE_DASH, font: FONT_ARIAL } )
    );
}

function competenceParagraph( competence, documentLanguage, accent ) {
    return para(
        [
            run( `${ competence.translations?.[ documentLanguage ] ?? "" } `, { pt: 8, font: FONT_ARIAL } ),
            ...dashRuns( competence.level, accent ),
        ],
        { spacing: { after: 0 } }
    );
}

export function buildCommonBusinessSkills( { data, props, accent } ) {
    const { businessSkills } = data;
    const { documentLanguage } = props;

    const categoryWidth = frac( 0.25 );
    const competencesWidth = frac( 0.75 );
    const subColWidth = Math.round( competencesWidth / 2 );

    const elements = [];

    businessSkills
        .slice()
        .sort( ( a, b ) => a.order - b.order )
        .forEach( category => {
            const competences = ( category.competences ?? [] )
                .slice()
                .sort( ( a, b ) => a.order - b.order );

            // Group competences two per row for the nested table.
            const nestedRows = [];
            for ( let i = 0; i < competences.length; i += 2 ) {
                const first = competenceParagraph( competences[ i ], documentLanguage, accent );
                const second = competences[ i + 1 ]
                    ? competenceParagraph( competences[ i + 1 ], documentLanguage, accent )
                    : para( [], { spacing: { after: 0 } } );
                nestedRows.push( new TableRow( {
                    children: [
                        cell( first, { widthTwips: subColWidth } ),
                        cell( second, { widthTwips: subColWidth } ),
                    ],
                } ) );
            }

            const nestedTable = nestedRows.length
                ? layoutTable( nestedRows, { width: competencesWidth, columnWidths: [ subColWidth, subColWidth ] } )
                : para( [], { spacing: { after: 0 } } );

            const categoryPara = para(
                run( category.category?.translations?.[ documentLanguage ] ?? "", { pt: 8, bold: true, color: accent, font: FONT_ARIAL } ),
                { spacing: { after: 0 } }
            );

            // The competences column holds a nested table; a cell must not end
            // with a table, so close it with a gap paragraph.
            const competencesCell = nestedRows.length ? [ nestedTable, tableGap() ] : [ nestedTable ];

            elements.push( layoutTable(
                [
                    new TableRow( {
                        children: [
                            cell( categoryPara, { widthTwips: categoryWidth, valign: VerticalAlign.TOP } ),
                            cell( competencesCell, { widthTwips: competencesWidth, valign: VerticalAlign.TOP } ),
                        ],
                    } ),
                ],
                { columnWidths: [ categoryWidth, competencesWidth ] }
            ) );
            elements.push( dividerRule( accent ) );
        } );

    return elements;
}
